/**
 * The image viewer (ADR-22 decision 7).
 *
 * One viewer per turn, opened from anywhere the turn's images are referenced — an inline embed,
 * an image chip, a file card, a row of the files table — at the item that was clicked. Before
 * this each surface opened its own single-image modal, so a response with four charts offered
 * four dead ends instead of one carousel.
 *
 * The provider owns the state and renders the carousel; the surfaces only say "open this id".
 * Without a provider (the style preview, a test rendering `MdContent` bare) `useArtifactViewer`
 * falls back to a carousel of one, so no surface depends on the provider being there.
 */

import {
  createContext,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActionIcon, Badge, Box, Button, Group, Image, Modal, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconDownload } from '@tabler/icons-react';
import { useTrackArtifactDownload } from '~portal/contexts';
import { downloadFile, trackAction } from '~portal/lib';
import { useArtifactSignedUrl } from '~portal/stores/artifact-store';
import { viewer } from './ArtifactViewer.css';
import { publishIntrinsicRatio } from './image-ratio';

/** @desc One slide: the artefact, and what the caption says about it. */
export type ViewerImage = {
  artifactId: string;
  /** @desc The description its author wrote at generation time, when there is one. */
  description?: string;
  /** @desc Shown as a `v<n>` badge past revision 1. */
  revision?: number;
  /** @desc What the download saves as — `<name>.<format>` where the transcript knows it. */
  downloadName?: string;
};

type OpenViewer = (artifactId: string) => void;

const ArtifactViewerContext = createContext<{
  images: readonly ViewerImage[];
  open: OpenViewer;
} | null>(null);

/**
 * @desc Opens the viewer for one artefact.
 *
 * `standalone` is the fallback carousel for a surface rendered outside a provider — render it
 * beside the trigger and it is null in the normal case, where the turn's provider owns the
 * viewer.
 */
export function useArtifactViewer(): { open: OpenViewer; standalone: ReactNode } {
  const ctx = useContext(ArtifactViewerContext);
  const [solo, setSolo] = useState<ViewerImage | null>(null);

  // Every surface reaches the carousel through this hook — the inline embed, the chip, the card
  // and the table row alike — so it is the one place the open is worth counting, whether the
  // turn's provider owns the viewer or the standalone fallback does.
  const open = useCallback<OpenViewer>(
    (artifactId) => {
      trackAction('artifact_viewer_opened', { standalone: ctx === null });
      return ctx ? ctx.open(artifactId) : setSolo({ artifactId });
    },
    [ctx],
  );

  return {
    open,
    standalone: solo ? (
      <ArtifactCarousel images={[solo]} initialIndex={0} onClose={() => setSolo(null)} />
    ) : null,
  };
}

export function ArtifactViewerProvider({
  images,
  children,
}: {
  /** @desc The turn's current image artefacts, in event order — the carousel's contents. */
  images: readonly ViewerImage[];
  children: ReactNode;
}) {
  const [opened, setOpened] = useState<{ images: readonly ViewerImage[]; index: number } | null>(
    null,
  );

  const open = useCallback<OpenViewer>(
    (artifactId) => {
      const index = images.findIndex((image) => image.artifactId === artifactId);
      // An image the turn does not list — a reference to an earlier turn's chart — still opens,
      // as a carousel of one. Better than refusing, and it cannot pretend to a place in a
      // sequence it is not part of.
      setOpened(index >= 0 ? { images, index } : { images: [{ artifactId }], index: 0 });
    },
    [images],
  );

  const value = useMemo(() => ({ images, open }), [images, open]);

  return (
    <ArtifactViewerContext.Provider value={value}>
      {children}
      {opened && (
        <ArtifactCarousel
          images={opened.images}
          initialIndex={opened.index}
          onClose={() => setOpened(null)}
        />
      )}
    </ArtifactViewerContext.Provider>
  );
}

/**
 * @desc The carousel itself: a scroll-snapping track of slides on a translucent black backdrop,
 * with floating controls.
 *
 * Navigation is the track's own scrolling — swipe and trackpad flicks need no handler of ours,
 * and the buttons and arrow keys only scroll it (styling: the platform's mechanism
 * first). The index is clamped rather than wrapped: a carousel of a turn's files is a short
 * finite list, and wrapping from the last back to the first reads as a loop the reader has to
 * count their way out of.
 */
function ArtifactCarousel({
  images,
  initialIndex,
  onClose,
}: {
  images: readonly ViewerImage[];
  initialIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const current = images[Math.min(index, images.length - 1)] ?? { artifactId: '' };

  /**
   * A scroll of our own, in flight, and where it is going.
   *
   * The track's `scroll` events are the only way to learn that the *reader* swiped — and a
   * scroll we started fires exactly the same events. Reading those intermediate offsets as
   * swipes dragged the index back mid-animation, so while `scrolling` is set the handler
   * reports nothing; it clears when the scroll arrives (`scrollend`, or within a pixel or two
   * of the target) or the moment the reader takes the track over.
   */
  const scrolling = useRef(false);
  const scrollTarget = useRef<number | null>(null);
  /**
   * The slide the track has actually been put on, or null while it has never been positioned.
   *
   * Until it has, every positioning is instant and no scroll event is believed: the viewer must
   * *appear* on the picture that was clicked, never glide to it from the first slide — which is
   * what a reader saw whenever the opening positioning landed on a retried frame, and what any
   * layout settling before then could still produce.
   */
  const positionedAt = useRef<number | null>(null);

  /**
   * The one place the track is scrolled — open and step alike, keyed on `index`.
   *
   * Two scroll paths were two chances to disagree, and they did: the open path worked while a
   * step left the caption describing a picture the track had never moved to. The offset comes
   * from the slide's own box rather than `index * clientWidth`, so it is right whatever the
   * padding and before the images have loaded, and the frame is retried until the modal has
   * finished laying out — a single pass at mount measures a zero-width track and gives up.
   */
  const position = useCallback((at: number) => {
    const track = trackRef.current;
    const slide = slideRefs.current[at];
    if (!track || !slide || track.clientWidth === 0) return false;

    const left = slide.offsetLeft - track.offsetLeft;
    // `instant` rather than `auto`: `auto` defers to the element's `scroll-behavior`, so an
    // opening positioning could still animate if anything above it ever sets `smooth`.
    const behavior: ScrollBehavior = positionedAt.current === null ? 'instant' : 'smooth';
    scrolling.current = true;
    scrollTarget.current = left;

    if (typeof track.scrollTo === 'function') track.scrollTo({ left, behavior });
    else track.scrollLeft = left;

    positionedAt.current = at;
    return true;
  }, []);

  /**
   * The track's ref: positioning it the instant the node exists, before the browser has painted
   * a frame of it, so there is no first paint at slide 0 to glide away from.
   */
  const attachTrack = useCallback(
    (node: HTMLDivElement | null) => {
      trackRef.current = node;
      if (node) position(initialIndex);
    },
    [initialIndex, position],
  );

  // Whatever the ref callback could not do — the modal is still laying out and the track
  // measures zero — is retried by frame, and stays instant until it lands.
  useEffect(() => {
    let frame = 0;
    let attempts = 0;

    const run = () => {
      // The ref callback positions the opening slide before the first paint whenever the track
      // is measurable that early; this effect is what covers the case where it is not.
      if (positionedAt.current === index || position(index)) return;
      if (attempts++ < 20) frame = requestAnimationFrame(run);
    };

    run();
    return () => cancelAnimationFrame(frame);
  }, [index, position]);

  // `scrollend` is the browser saying the animation finished — the reliable end of a scroll of
  // ours, where the target comparison is only a good approximation of it.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const settled = () => {
      scrolling.current = false;
    };
    track.addEventListener('scrollend', settled);
    return () => track.removeEventListener('scrollend', settled);
  }, []);

  const step = useCallback(
    (delta: number) => setIndex((at) => Math.min(Math.max(at + delta, 0), images.length - 1)),
    [images.length],
  );

  // Arrow keys are the carousel's, wherever focus sits inside the modal. Escape is Mantine's.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step]);

  // Slides publish the URL they resolved, so the download control can act on the current one
  // without resolving it a second time. A lazily created state value rather than `ref.current`:
  // the map is handed to the slides during render, and a ref read in render is opaque to the
  // React Compiler. It is mutated in place and never set, so it never causes a re-render.
  const [signedUrls] = useState(() => new Map<string, string>());

  const trackDownload = useTrackArtifactDownload();
  const download = () => {
    const url = signedUrls.get(current.artifactId);
    if (!url) return;
    trackDownload(current.downloadName?.split('.').pop() ?? 'image', 'viewer');
    downloadFile(url, current.downloadName);
  };

  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      withOverlay={false}
      padding={0}
      transitionProps={{ transition: 'fade', duration: 120 }}
      aria-label="Image viewer"
      classNames={{ content: viewer.backdrop, body: viewer.backdrop }}
    >
      {/* The backdrop is the way out: a click anywhere the page shows through closes the
          viewer. Everything that is *content* — the picture, the caption row, the controls —
          carries `data-viewer-keep`, so the test is what was hit rather than a stack of
          stopPropagation calls spread across every child. */}
      <Box
        className={viewer.backdropSurface}
        data-testid="viewer-backdrop"
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('[data-viewer-keep]')) onClose();
        }}
      >
        <Box className={viewer.stage}>
          <Box
            ref={attachTrack}
            className={viewer.track}
            // The reader taking the track over ends any scroll of ours, so a swipe interrupting an
            // animation is followed rather than fought.
            onPointerDown={() => {
              scrolling.current = false;
            }}
            onTouchStart={() => {
              scrolling.current = false;
            }}
            onWheel={() => {
              scrolling.current = false;
            }}
            onScroll={(event) => {
              const track = event.currentTarget;
              // A scroll of ours reports its progress exactly as a swipe does; only its arrival
              // is news, and it is news the index already knows.
              // Nothing the track says before it has been positioned is the reader's doing — it
              // is the modal laying itself out, and believing it moved the viewer off the
              // picture that was clicked.
              if (scrolling.current || positionedAt.current === null) {
                if (
                  scrollTarget.current === null ||
                  Math.abs(track.scrollLeft - scrollTarget.current) < 2
                ) {
                  scrolling.current = false;
                }
                return;
              }
              if (track.clientWidth === 0) return;
              const at = Math.round(track.scrollLeft / track.clientWidth);
              setIndex(Math.min(Math.max(at, 0), images.length - 1));
            }}
          >
            {images.map((image, slideIndex) => {
              const isCurrent = slideIndex === index;
              // Applied to every element that must disappear from assistive tech and the
              // pointer on a non-current slide, without disappearing from layout (H2-222) —
              // `viewer.hidden` (visibility, not display/mount) does the layout side, these do
              // the a11y/interaction side. Exactly one slide's set carries neither.
              const hiddenProps = isCurrent ? {} : { 'aria-hidden': true as const, inert: true };

              return (
                <Box
                  key={image.artifactId}
                  className={viewer.slide}
                  data-slide-index={slideIndex}
                  ref={(node: HTMLDivElement | null) => {
                    slideRefs.current[slideIndex] = node;
                  }}
                >
                  <ViewerSlide
                    image={image}
                    urls={signedUrls}
                    /*
                     * The controls and caption belong to the picture, so every slide's figure
                     * carries its own copy — not only the current slide's. Mounting them only on
                     * the current slide (the pre-H2-222 shape) meant the outgoing slide's figure
                     * lost this row the instant a step began, mid-scroll, shrinking its box and
                     * jumping the picture vertically; the incoming slide gained the row and
                     * jumped the other way. Keeping every slide's shape identical and only
                     * toggling visibility/aria-hidden/inert fixes the layout while still exposing
                     * exactly one set of labels.
                     */
                    controls={
                      images.length > 1 ? (
                        <>
                          {/* No close control: the backdrop click and Escape are the ways out, and
                          a button beside the picture only ever covered the lockup in its corner. */}
                          <ActionIcon
                            variant="subtle"
                            size="lg"
                            className={`${viewer.control} ${viewer.step} ${viewer.stepPrev}${
                              isCurrent ? '' : ` ${viewer.hidden}`
                            }`}
                            aria-label="Previous image"
                            disabled={slideIndex === 0}
                            onClick={() => step(-1)}
                            {...hiddenProps}
                          >
                            <IconChevronLeft size={28} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            size="lg"
                            className={`${viewer.control} ${viewer.step} ${viewer.stepNext}${
                              isCurrent ? '' : ` ${viewer.hidden}`
                            }`}
                            aria-label="Next image"
                            disabled={slideIndex === images.length - 1}
                            onClick={() => step(1)}
                            {...hiddenProps}
                          >
                            <IconChevronRight size={28} />
                          </ActionIcon>
                        </>
                      ) : null
                    }
                    caption={
                      <Box
                        className={`${viewer.caption}${isCurrent ? '' : ` ${viewer.hidden}`}`}
                        {...hiddenProps}
                      >
                        <Box className={viewer.captionText}>
                          <Group gap={8} wrap="nowrap">
                            {image.description && (
                              <Text size="sm" c="white">
                                {image.description}
                              </Text>
                            )}
                            {image.revision !== undefined && image.revision > 1 && (
                              <Badge size="sm" variant="light" color="gray">
                                v{image.revision}
                              </Badge>
                            )}
                          </Group>
                          {images.length > 1 && (
                            <Text size="xs" c="gray.4">
                              {slideIndex + 1} / {images.length}
                            </Text>
                          )}
                        </Box>

                        <Button
                          leftSection={<IconDownload size={16} />}
                          data-dd-action-name="Download file"
                          onClick={download}
                        >
                          Download
                        </Button>
                      </Box>
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

/**
 * @desc One slide: the picture, wearing the carousel's controls as its own frame.
 *
 * `controls` are positioned against the picture's box and `caption` sits under it — both are
 * passed in rather than rendered here, because they act on the *carousel* (step, close,
 * download the current file) and only their placement belongs to the slide. A slide that cannot
 * show its picture still wears them: losing the way out with the image would be the worse
 * failure.
 */
function ViewerSlide({
  image,
  urls,
  controls,
  caption,
}: {
  image: ViewerImage;
  urls: Map<string, string>;
  controls: ReactNode;
  caption: ReactNode;
}) {
  const { url, isLoading } = useArtifactSignedUrl(image.artifactId);

  // The carousel's download control acts on whatever the current slide resolved, so each slide
  // records its own URL rather than the control resolving the artefact a second time.
  useEffect(() => {
    if (url) urls.set(image.artifactId, url);
  }, [url, urls, image.artifactId]);

  /**
   * The figure is `fit-content`, and fit-content resolves from the picture's INTRINSIC width —
   * 1230px for a 1230px file — not from the capped width the image actually renders at (615px on
   * a 2x display). Left alone, the controls hang on a box twice as wide as the picture. So the
   * native-size ceiling the image publishes is copied onto the figure, which caps itself on it.
   */
  const publishFigureSize = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    publishIntrinsicRatio(event);
    const img = event.currentTarget;
    const figure = img.closest<HTMLElement>('[data-viewer-keep]');
    if (!figure) return;
    for (const name of ['--img-native-w', '--img-native-h']) {
      const value = img.style.getPropertyValue(name);
      if (value) figure.style.setProperty(name, value);
    }
  }, []);

  const picture = isLoading ? (
    <Text size="sm" c="gray.4">
      Loading image…
    </Text>
  ) : url ? (
    <Image
      src={url}
      alt={image.description ?? 'Full size artifact'}
      w="auto"
      h="auto"
      fit="none"
      className={viewer.image}
      onLoad={publishFigureSize}
    />
  ) : (
    <Text size="sm" c="red.4">
      This image could not be loaded.
    </Text>
  );

  return (
    <Box className={viewer.figure} data-viewer-keep>
      <Box className={viewer.frame}>
        {picture}
        {controls}
      </Box>
      {caption}
    </Box>
  );
}
